import { Link } from "react-router-dom";
import { RichText } from "@/components/RichText";
import type { Section } from "@/lib/types";

function SectionHeader({ eyebrow, title, sub }: { eyebrow?: string; title?: string; sub?: string }) {
  if (!eyebrow && !title && !sub) return null;
  return (
    <div className="section-header">
      {eyebrow && <span className="section-eyebrow">{eyebrow}</span>}
      {title && <RichText as="h2" className="section-title" html={title} />}
      {sub && <p className="section-sub">{sub}</p>}
    </div>
  );
}

export default function SectionRenderer({ section }: { section: Section }) {
  switch (section._type) {
    case "capGrid":
      return (
        <section className="sec sec-cream">
          <div className="sec-inner">
            <SectionHeader eyebrow={section.eyebrow} title={section.title} sub={section.sub} />
            <div className="cap-grid">
              {section.items.map((item, i) => (
                <div className="cap-item" key={i}>
                  <span className="cap-num">{item.badge}</span>
                  <h3 className="cap-title">{item.title}</h3>
                  <p className="cap-body">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case "outcomesBand":
      return (
        <section className="outcomes-band">
          <div className="outcomes-inner">
            <SectionHeader eyebrow={section.eyebrow} title={section.title} sub={section.sub} />
            <div className="outcomes-grid">
              {section.stats.map((s, i) => (
                <div className="outcome-stat" key={i}>
                  <div className="outcome-num">{s.num}</div>
                  <div className="outcome-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case "flow":
      return (
        <section className="sec sec-cream-soft">
          <div className="sec-inner">
            <SectionHeader eyebrow={section.eyebrow} title={section.title} sub={section.sub} />
            <div className="flow">
              {section.steps.map((step, i) => (
                <div className="flow-step" key={i}>
                  <div className="flow-num">{step.num}</div>
                  <div className="flow-title">{step.title}</div>
                  <p className="flow-body">{step.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case "proofBox":
      return (
        <section className="sec sec-cream">
          <div className="sec-inner">
            <SectionHeader eyebrow={section.data.eyebrow} title={section.data.title} />
            <div className="proof-box">
              <div className="proof-quote-wrap">
                <span className="proof-customer">{section.data.customer}</span>
                <div className="proof-quote">{section.data.quote}</div>
                <div className="proof-attr">{section.data.attribution}</div>
              </div>
              <div className="proof-stats">
                {section.data.stats.map((s, i) => (
                  <div key={i}>
                    <div className="proof-stat-big">{s.big}</div>
                    <div className="proof-stat-label">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      );

    case "connect":
      return (
        <section className="connect">
          <div className="connect-inner">
            <div>
              <RichText as="h2" className="connect-title" html={section.data.title} />
              <p className="connect-body">{section.data.body}</p>
            </div>
            <div className="connect-agents">
              {section.data.agents.map((a) => (
                <Link to={a.href} className="connect-agent" key={a.name}>
                  <span className="connect-agent-name">{a.name}</span>
                  <span className="connect-agent-role">{a.role}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      );

    case "prose":
      return (
        <section className="sec sec-cream-soft">
          <div className="sec-inner" style={{ maxWidth: 880 }}>
            <SectionHeader eyebrow={section.eyebrow} title={section.title} sub={section.sub} />
            <p className="section-sub" style={{ marginTop: 16 }}>
              {section.body}
            </p>
          </div>
        </section>
      );

    default:
      return null;
  }
}
